import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, lastValueFrom, Observable, Subject} from 'rxjs';
import {AuthService} from 'src/app/shared/services/auth.service';
import {environment} from 'src/environments/environment';
import {getDoc, getDocs, limit, orderBy, query, setDoc, updateDoc, where, Unsubscribe} from 'firebase/firestore';
import {FirebaseService} from 'src/app/services/firebase.service';
import {ChatChannelConfig} from './chat-channel.types';

export const CHAT_SOPORTE_CONFIG: ChatChannelConfig = {
    collectionRoot: 'chat-soporte',
    apiBasePath: 'api/chat-soporte',
    headerTitle: 'Soporte simyo',
    toastText: 'Tienes nuevos mensajes de soporte simyo',
    iconSrc: 'assets/img/chat-soporte.svg'
};

@Injectable({
    providedIn: 'root'
})
export class ChatSoporteService {
    private readonly collectionRoot = CHAT_SOPORTE_CONFIG.collectionRoot;
    private readonly apiBasePath = CHAT_SOPORTE_CONFIG.apiBasePath;

    private requestToggleChatSubject: Subject<void> = new Subject<void>();
    private openChatSubject: Subject<boolean> = new Subject<boolean>();
    private minimizedChatSubject: Subject<boolean> = new Subject<boolean>();
    private unreadCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    /**
     * false al entrar → icono solo por permiso.
     * true cuando en línea el chat está archivado → ocultar icono.
     */
    private chatArchivedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    readonly config = CHAT_SOPORTE_CONFIG;

    constructor(
        private auth: AuthService,
        private http: HttpClient,
        private firebaseService: FirebaseService
    ) {
    }

    private getHeaders(): HttpHeaders {
        const token = 'Bearer ' + this.auth.getToken();

        return new HttpHeaders({
            Authorization: token,
            'Content-Type': 'application/json'
        });
    }

    public getHeadersWithoutContentType(): HttpHeaders {
        return new HttpHeaders({
            Authorization: 'Bearer ' + this.auth.getToken()
        });
    }

    async markMessagesAsRead(id: string) {
        const chatRef = await this.firebaseService.getDocument(`${this.collectionRoot}/${id}`);
        const messagesRef = await this.firebaseService.getCollection(`${this.collectionRoot}/${id}/messages`);
        const path = 'pdv_read';
        const batch = await this.firebaseService.getWriteBatch();

        const unreadQuery = query(
            messagesRef,
            orderBy('created_at', 'desc'),
            where(path, '==', false),
            limit(500)
        );
        const snapshot = await getDocs(unreadQuery);

        batch.update(chatRef, {['last_message_' + path]: true});
        snapshot.docs.forEach(docSnap => batch.update(docSnap.ref, {[path]: true}));

        await batch.commit();
    }

    async createChatMessage(message: any, file: File | null = null) {
        const chatRef = await this.firebaseService.getDocument(`${this.collectionRoot}/${message.chat_id}`);
        const messageRef = await this.firebaseService.getDocument(`${this.collectionRoot}/${message.chat_id}/messages/${message.id}`);

        await setDoc(messageRef, message);
        await updateDoc(chatRef, {
            last_message_text: file != null ? file.name : message.message,
            last_message_date: message.created_at,
            last_message_user_support_read: message.user_support_read === true,
            last_message_pdv_read: message.pdv_read === true
        });
    }

    sendChatPushNotification(message: any, file: File | null = null) {
        const body = {
            sender_id: message.sender_id,
            sender_name: message.sender_name,
            sender_type: message.sender_type,
            message: file != null ? file.name : message.message
        };

        this.http.post<any>(
            environment.msDigoApp + `${this.apiBasePath}/${message.chat_id}/send-push-notification`,
            body,
            {headers: this.getHeaders()}
        ).subscribe({
            next: () => {},
            error: () => {}
        });
    }

    sendChatMessageFile(message: any, file: File) {
        const formData = new FormData();
        formData.append('file', file, file.name);

        return this.http.post<any>(
            environment.msDigoApp + `${this.apiBasePath}/${message.chat_id}/message/${message.id}/upload-file`,
            formData,
            {headers: this.getHeadersWithoutContentType()}
        );
    }

    requestToggleChat() {
        this.requestToggleChatSubject.next();
    }

    get requestToggleChat$(): Observable<void> {
        return this.requestToggleChatSubject.asObservable();
    }

    openChat(value: boolean): void {
        this.openChatSubject.next(value);
    }

    get openChat$(): Observable<boolean> {
        return this.openChatSubject.asObservable();
    }

    minimizedChat(value: boolean): void {
        this.minimizedChatSubject.next(value);
    }

    get minimizedChat$(): Observable<boolean> {
        return this.minimizedChatSubject.asObservable();
    }

    updateUnreadCount(count: number): void {
        this.unreadCountSubject.next(count);
    }

    get unreadCount$(): Observable<number> {
        return this.unreadCountSubject.asObservable();
    }

    setChatArchived(archived: boolean): void {
        this.chatArchivedSubject.next(archived);
    }

    get chatArchived$(): Observable<boolean> {
        return this.chatArchivedSubject.asObservable();
    }

    get isChatArchived(): boolean {
        return this.chatArchivedSubject.getValue();
    }

    getPdvChat(id: any): Observable<any> {
        return this.http.get<any>(
            environment.msDigoApp + `${this.apiBasePath}/pdv/${id}`,
            {headers: this.getHeaders()}
        );
    }

    registerFcmToken(body: any): Observable<any> {
        return this.http.post<any>(
            environment.msDigoApp + `${this.apiBasePath}/register-fcm-token`,
            body,
            {headers: this.getHeaders()}
        );
    }

    async removeFcmToken(pdv: any) {
        if (!pdv?.info?.idpdv) {
            return;
        }
        const token = await this.firebaseService.registerMessagingToken();
        if (!token) {
            return;
        }
        const body = {
            id_pdv: pdv.info.idpdv,
            token: token
        };

        try {
            await lastValueFrom(
                this.http.post<any>(
                    environment.msDigoApp + `${this.apiBasePath}/remove-fcm-token`,
                    body,
                    {headers: this.getHeaders()}
                )
            );
        } catch {
            // No bloquear el logout si el backend falla
        }
    }

    createChatRemote(id_pdv: any): Observable<any> {
        return this.http.post<any>(
            environment.msDigoApp + `${this.apiBasePath}/create`,
            {id_pdv},
            {headers: this.getHeaders()}
        );
    }

    async deleteMessage(chatId: string, messageId: string) {
        await this.firebaseService.deleteDocument(`${this.collectionRoot}/${chatId}/messages/${messageId}`);

        let data: any;
        const chatRef = await this.firebaseService.getDocument(`${this.collectionRoot}/${chatId}`);
        const lastMessage = await this.firebaseService.getLastMessage(chatId, this.collectionRoot);

        if (lastMessage != null) {
            data = {
                last_message_text: lastMessage.message,
                last_message_date: lastMessage.created_at,
                last_message_user_support_read: lastMessage.user_support_read === true,
                last_message_pdv_read: lastMessage.pdv_read === true,
            };
        } else {
            const chat = await getDoc(chatRef);

            data = {
                last_message_text: '',
                last_message_date: chat.data()?.['created_at'],
                last_message_user_support_read: null,
                last_message_pdv_read: null,
            };
        }

        await updateDoc(chatRef, data);
    }

    onNewChat(pdv_id: any, handler: (chat: any) => void) {
        let unsubscribe: Unsubscribe | null = null;

        this.firebaseService.getNewChat(pdv_id, (snapshot) => {
            if (!snapshot.empty) {
                handler({id: snapshot.docs[0].id, ...snapshot.docs[0].data()});
                unsubscribe?.();
            }
        }, this.collectionRoot).then((sub) => unsubscribe = sub);
    }
}
